import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, CheckCircle2, Clock, MessageSquarePlus, Trash2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getToken = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

export default function PendingWorkPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [newTask, setNewTask] = useState("");
  const [remarkText, setRemarkText] = useState("");
  const [remarkTaskId, setRemarkTaskId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  // Check access: only Divyang allowed
  useEffect(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        if (user.username === 'Divyang') {
          setAuthorized(true);
          return;
        }
      }
    } catch (e) {}
    navigate('/', { replace: true });
  }, [navigate]);

  const fetchTasks = useCallback(async () => {
    if (!authorized) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/pending-work`, { headers: headers() });
      setTasks(res.data);
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
    } finally {
      setLoading(false);
    }
  }, [authorized]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Daily pop-up logic: show once per day on page load
  useEffect(() => {
    if (!loading && tasks.length > 0) {
      const pendingTasks = tasks.filter(t => t.status === "pending");
      if (pendingTasks.length > 0) {
        const today = new Date().toDateString();
        const lastShown = localStorage.getItem("pending_work_popup_date");
        if (lastShown !== today) {
          setShowPopup(true);
          localStorage.setItem("pending_work_popup_date", today);
        }
      }
    }
  }, [loading, tasks]);

  const addTask = async () => {
    if (!newTask.trim()) return;
    try {
      await axios.post(`${API}/pending-work`, { description: newTask.trim() }, { headers: headers() });
      setNewTask("");
      fetchTasks();
    } catch (err) {
      console.error("Failed to add task:", err);
    }
  };

  const markCompleted = async (taskId) => {
    try {
      await axios.put(`${API}/pending-work/${taskId}/status`, { status: "completed" }, { headers: headers() });
      fetchTasks();
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const markPending = async (taskId) => {
    try {
      await axios.put(`${API}/pending-work/${taskId}/status`, { status: "pending" }, { headers: headers() });
      fetchTasks();
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const addRemark = async () => {
    if (!remarkText.trim() || !remarkTaskId) return;
    try {
      await axios.put(`${API}/pending-work/${remarkTaskId}/remark`, { remark: remarkText.trim() }, { headers: headers() });
      setRemarkText("");
      setRemarkTaskId(null);
      fetchTasks();
    } catch (err) {
      console.error("Failed to add remark:", err);
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await axios.delete(`${API}/pending-work/${taskId}`, { headers: headers() });
      fetchTasks();
    } catch (err) {
      console.error("Failed to delete task:", err);
    }
  };

  const pendingTasks = tasks.filter(t => t.status === "pending");
  const completedTasks = tasks.filter(t => t.status === "completed");
  const displayTasks = activeTab === "pending" ? pendingTasks : completedTasks;

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  if (!authorized) return null;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="pending-work-page">
      <h1 className="text-2xl font-bold mb-1">Pending Work</h1>
      <p className="text-sm text-muted-foreground mb-6">Track and manage your pending tasks</p>

      {/* Add new task */}
      <div className="flex gap-2 mb-6" data-testid="add-task-form">
        <Input
          data-testid="new-task-input"
          placeholder="Enter pending work description..."
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addTask()}
          className="flex-1"
        />
        <Button data-testid="add-task-btn" onClick={addTask} disabled={!newTask.trim()}>
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b" data-testid="work-tabs">
        <button
          data-testid="pending-tab"
          onClick={() => setActiveTab("pending")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "pending"
              ? "border-red-500 text-red-600"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Clock className="w-4 h-4 inline mr-1" />
          Pending ({pendingTasks.length})
        </button>
        <button
          data-testid="completed-tab"
          onClick={() => setActiveTab("completed")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "completed"
              ? "border-green-500 text-green-600"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <CheckCircle2 className="w-4 h-4 inline mr-1" />
          Completed ({completedTasks.length})
        </button>
      </div>

      {/* Task list */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : displayTasks.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="empty-state">
          {activeTab === "pending" ? "No pending tasks. You're all caught up!" : "No completed tasks yet."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="task-list">
          {displayTasks.map((task) => (
            <div
              key={task.id}
              data-testid={`task-card-${task.id}`}
              className={`border rounded-lg p-4 ${
                task.status === "pending"
                  ? "border-red-300 bg-red-50"
                  : "border-green-300 bg-green-50"
              }`}
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-start gap-2">
                  {task.status === "pending" ? (
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span
                      className={`font-semibold text-sm ${
                        task.status === "pending" ? "text-red-800" : "text-green-800 line-through"
                      }`}
                    >
                      {task.description}
                    </span>
                    <div className="text-xs text-muted-foreground mt-1">
                      Added: {formatDate(task.created_at)}
                      {task.completed_at && <span className="ml-3">Completed: {formatDate(task.completed_at)}</span>}
                    </div>
                  </div>
                </div>

                {/* Remarks section */}
                {task.remarks && task.remarks.length > 0 && (
                  <div className="ml-6 space-y-1">
                    {task.remarks.map((r) => (
                      <div key={r.id} className="flex items-start gap-2 text-xs bg-white/70 rounded px-2 py-1 border border-amber-200">
                        <MessageSquarePlus className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-amber-800 font-medium">{r.text}</span>
                          <span className="text-muted-foreground ml-2">({formatDate(r.added_at)})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Action buttons - always at bottom */}
                <div className="flex items-center gap-1 ml-6 flex-wrap">
                  {task.status === "pending" ? (
                    <>
                      <Button
                        data-testid={`complete-btn-${task.id}`}
                        size="sm"
                        variant="outline"
                        className="text-green-600 border-green-300 hover:bg-green-100 text-xs h-7"
                        onClick={() => markCompleted(task.id)}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                      </Button>
                      <Button
                        data-testid={`remark-btn-${task.id}`}
                        size="sm"
                        variant="outline"
                        className="text-amber-600 border-amber-300 hover:bg-amber-100 text-xs h-7"
                        onClick={() => setRemarkTaskId(task.id)}
                      >
                        <MessageSquarePlus className="w-3 h-3 mr-1" /> Remark
                      </Button>
                    </>
                  ) : (
                    <Button
                      data-testid={`reopen-btn-${task.id}`}
                      size="sm"
                      variant="outline"
                      className="text-orange-600 border-orange-300 hover:bg-orange-100 text-xs h-7"
                      onClick={() => markPending(task.id)}
                    >
                      <Clock className="w-3 h-3 mr-1" /> Re-open
                    </Button>
                  )}
                  <Button
                    data-testid={`delete-btn-${task.id}`}
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground hover:text-red-500 text-xs h-7"
                    onClick={() => deleteTask(task.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Remark Dialog */}
      <Dialog open={!!remarkTaskId} onOpenChange={(open) => !open && setRemarkTaskId(null)}>
        <DialogContent data-testid="remark-dialog" aria-describedby="remark-description">
          <DialogHeader>
            <DialogTitle>Add Remark</DialogTitle>
          </DialogHeader>
          <p id="remark-description" className="sr-only">Enter a remark for this pending task</p>
          <Textarea
            data-testid="remark-input"
            placeholder="Enter your remark..."
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemarkTaskId(null)}>Cancel</Button>
            <Button data-testid="save-remark-btn" onClick={addRemark} disabled={!remarkText.trim()}>
              Save Remark
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Daily Pop-up for Pending Work */}
      <Dialog open={showPopup} onOpenChange={setShowPopup}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="daily-popup" aria-describedby="popup-description">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> Daily Pending Work Reminder
            </DialogTitle>
          </DialogHeader>
          <p id="popup-description" className="text-sm text-muted-foreground mb-3">
            You have <span className="font-bold text-red-600">{pendingTasks.length}</span> pending task(s) to complete:
          </p>
          <div className="space-y-2">
            {pendingTasks.map((task, idx) => (
              <div key={task.id} className="border border-red-300 bg-red-50 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <span className="text-red-600 font-bold text-sm">{idx + 1}.</span>
                  <div className="flex-1">
                    <span className="text-red-800 font-semibold text-sm">{task.description}</span>
                    <div className="text-xs text-muted-foreground mt-0.5">Added: {formatDate(task.created_at)}</div>
                    {task.remarks && task.remarks.length > 0 && (
                      <div className="mt-1 text-xs text-amber-700">
                        Last remark: {task.remarks[task.remarks.length - 1].text}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="close-popup-btn" onClick={() => setShowPopup(false)}>
              Got it, Let's Work!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
